/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CommonActions, FormActionPipelines, FormFlowState } from '@gh/builder-shared';
import { BuilderPermission, HousePlanStyle } from '@gh/builder-shared-data';
import {
  EnumDictionary,
  FormState,
  FormStateBuilder,
  getDialogInitData,
  UserContext,
  whenCreateOrEditMode,
} from '@gh/common-shared';
import { DialogInput, FormMode } from '@gh/core-ui';
import { assign, isNil } from 'lodash';
import {
  HousePlanDetailsDialogConfig,
  HousePlanListBusinessService,
} from '../../resources/house-plan-list-business.service';
import { HousePlanDetails } from '../../resources/house-plans.model';

@Component({
  standalone: false,
  selector: 'gh-house-plan-details-dialog',
  templateUrl: 'house-plan-details-dialog.component.html',
  styleUrls: ['house-plan-details-dialog.component.scss'],
})
export class HousePlanDetailsDialogComponent implements DialogInput<HousePlanDetailsDialogConfig>, OnInit {
  input: HousePlanDetailsDialogConfig;

  form: FormState<HousePlanDetails>;

  housePlanStyles: EnumDictionary<HousePlanStyle>;

  constructor(@Inject(MAT_DIALOG_DATA) private dialogData: any,
              private userContext: UserContext,
              private formStateBuilder: FormStateBuilder,
              private businessService: HousePlanListBusinessService,
              private dialogRef: MatDialogRef<any>,
              private pipelines: FormActionPipelines,
              private actions: CommonActions) {
  }

  get title(): string {
    return {
      [FormMode.CreateMode]: 'lbl_hp_create',
      [FormMode.EditMode]: 'lbl_hp_edit',
      [FormMode.ViewMode]: 'lbl_hp_view',
    }[this.form.mode];
  }

  ngOnInit(): void {
    const { housePlan, ...init } = getDialogInitData(this.dialogData);

    assign(this, init);

    const editOrViewMode = housePlan.allowForView
    || !this.userContext.hasPermission(BuilderPermission.Estimating.HouseEstimate.Plans.EDIT)
      ? FormMode.ViewMode : FormMode.EditMode;
    const mode = isNil(housePlan.housePlanId) ? FormMode.CreateMode : editOrViewMode;

    this.form = this.formStateBuilder.buildPlain<HousePlanDetails>({
      value: housePlan,
      mode,
      enabled: whenCreateOrEditMode(),
      children: {
        housePlanId: void 0,
        code: { required: true },
        style: { required: true },
        name: { required: true },
      },
    });
  }

  onSubmit(): void {
    const flow = this.pipelines.process({
      execute: this.actions.execute((state: FormFlowState<HousePlanDetails>) => this.businessService.save(state.value)),
      finish: (state) => this.dialogRef.close(state.value),
    });

    flow.run({ form: this.form, value: this.form.submitValue });
  }

  onCancel(): void {
    this.dialogRef.close();
  }
}
